/**
 * ChangeControlDetail — full view of one GMP change request (change_control).
 * Parts A/B/C/E + QRA/FMEA + approvals + effectiveness + Part-11 history timeline.
 * Route: /change-control/$id. Bilingual (en-US / ar-SA).
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { downloadAuthed } from '../../api/client';
import styles from '../Recalls/Recalls.module.css';
import cc from './ChangeControlDetail.module.css';
import {
  useChangeRequest, useTransitionCr, useAddImpact, useDeleteImpact, useAddQra, useDeleteQra,
  useAddApproval, useDeleteApproval, useAddEffectiveness, useDeleteEffectiveness,
  useAddAction, useConvertActions, usePatchAction, useDeleteAction,
  useAddEvidence, useDeleteEvidence, useChangeTypes,
  useAddSubAssessment, useGenerateSubAssessments, usePatchSubAssessment, useDeleteSubAssessment,
  useAuditTrail, useAddCommittee, usePatchCommittee, useDeleteCommittee,
  CC_FSM, CC_REASON_REQUIRED, CC_DEPARTMENTS, CC_QRA_DECISIONS, rpnCategory,
  CC_APPROVAL_STAGES, CC_APPROVAL_DECISIONS, CC_EFF_OUTCOMES, CC_ACTION_FSM, CC_ACTION_CATEGORIES,
  CC_EVIDENCE_CATEGORIES, CC_SUB_KINDS, CC_SUB_STATUS, CC_VALIDATION_TYPES, CC_TRAINING_TYPES,
  CC_COMMITTEE_ROLES, CC_COMMITTEE_VOTES, CC_STATUS_LABEL, CC_LEVEL_LABEL,
} from './useChangeControl';
import type { CrSubAssessmentRow, NewSubAssessmentInput } from './useChangeControl';

const SOD = [1, 2, 3, 4, 5];
function rpnClass(cls: Record<string, string>, rpn: number) {
  const c = rpnCategory(rpn);
  return c === 'LOW' ? cls['rpnLow'] : c === 'MODERATE' ? cls['rpnMod'] : cls['rpnHigh'];
}
function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function fmtDT(iso?: string | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
}
const s = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));
function levelClass(l: string | null) {
  return l === 'LEVEL_3' ? styles.cls1 : l === 'LEVEL_2' ? styles.cls2 : styles.cls3;
}

export function ChangeControlDetail({ id }: { id: string }) {
  const q = useChangeRequest(id);
  const { t } = useTranslation();
  const transition = useTransitionCr(id);
  const addImpact = useAddImpact(id);
  const delImpact = useDeleteImpact(id);
  const addQra = useAddQra(id);
  const delQra = useDeleteQra(id);
  const addAppr = useAddApproval(id);
  const delAppr = useDeleteApproval(id);
  const addEff = useAddEffectiveness(id);
  const delEff = useDeleteEffectiveness(id);
  const addAction = useAddAction(id);
  const convertActions = useConvertActions(id);
  const patchAction = usePatchAction(id);
  const delAction = useDeleteAction(id);
  const addEvidence = useAddEvidence(id);
  const delEvidence = useDeleteEvidence(id);
  const typesQ = useChangeTypes();
  const addSub = useAddSubAssessment(id);
  const genSub = useGenerateSubAssessments(id);
  const patchSub = usePatchSubAssessment(id);
  const delSub = useDeleteSubAssessment(id);
  const [showAudit, setShowAudit] = useState(false);
  const auditQ = useAuditTrail(id, showAudit);
  const addCommittee = useAddCommittee(id);
  const patchCommittee = usePatchCommittee(id);
  const delCommittee = useDeleteCommittee(id);
  const [reason, setReason] = useState('');
  const [actionForm, setActionForm] = useState({ description: '', category: 'VALIDATION', owner_name: '', target_date: '' });
  const [impactForm, setImpactForm] = useState({ department: 'PRODUCTION', has_impact: 'true', assessment_text: '', target_date: '', owner_name: '' });
  const [qraForm, setQraForm] = useState({ subject: '', severity: '3', occurrence: '3', detectability: '3', mitigation: '', rsev: '', rocc: '', rdet: '', decision: '' });
  const [apprForm, setApprForm] = useState({ stage: 'QA_MANAGER', decision: 'APPROVED', approver_name: '', comments: '' });
  const [effForm, setEffForm] = useState({ review_due_date: '', outcome: 'EFFECTIVE', objective_achieved: 'true', capa_ref: '' });
  const [evForm, setEvForm] = useState({ category: 'VALIDATION', title: '', notes: '' });
  const [evFile, setEvFile] = useState<{ name: string; mime: string; base64: string } | null>(null);
  const [subForm, setSubForm] = useState({
    kind: 'VALIDATION', title: '', owner_name: '', target_date: '',
    variation_type: '', country: '', authority: '', submission_before_impl: false,
    validation_type: 'OQ', protocol_ref: '', report_ref: '',
    audience: '', training_type: 'READ_UNDERSTAND', document_ref: '',
  });
  const [commForm, setCommForm] = useState({ member_name: '', member_role: 'MEMBER' });

  // i18n helpers — `td` = changeControl.detail.*; the rest map enum codes.
  const td = (k: string, opts?: Record<string, unknown>) => t(`changeControl.detail.${k}`, opts ?? {});
  const tStatus = (st: string) => t(`changeControl.status.${st}`, { defaultValue: CC_STATUS_LABEL[st] ?? st });
  const tLevel = (l: string) => t(`changeControl.level.${l}`, { defaultValue: CC_LEVEL_LABEL[l] ?? l });
  const tEnum = (ns: string, v: unknown) => {
    const sv = v === null || v === undefined || v === '' ? '' : String(v);
    return sv ? t(`changeControl.${ns}.${sv}`, { defaultValue: sv.replaceAll('_', ' ') }) : '—';
  };
  const yn = (v: unknown) => (v === true ? td('yes') : v === false ? td('no') : '—');

  if (q.isLoading) return <main className={styles.page}><div className={styles.empty}>{td('loading')}</div></main>;
  if (q.isError || !q.data) return <main className={styles.page}><div className={styles.empty}>{td('notFound')} <Link to="/change-control">{td('backToRegister')}</Link></div></main>;
  const r = q.data;
  const nextStates = CC_FSM[r.status] ?? [];
  const LOCKED_STATES = ['CLOSED_APPROVED', 'CANCELLED', 'REJECTED'];
  const isLocked = LOCKED_STATES.includes(r.status);
  // A controlled reopen (CLOSED_APPROVED -> IMPLEMENTATION_IN_PROGRESS) needs a reason.
  const needsReason = (to: string) => CC_REASON_REQUIRED.has(to) || (r.status === 'CLOSED_APPROVED' && to === 'IMPLEMENTATION_IN_PROGRESS');
  async function doTransition(to: string) {
    if (needsReason(to) && !reason.trim()) return;
    try { await transition.mutateAsync({ to, reason: reason.trim() || undefined }); setReason(''); }
    catch { /* surfaced via transition.isError */ }
  }
  async function submitImpact() {
    try {
      await addImpact.mutateAsync({
        department: impactForm.department,
        has_impact: impactForm.has_impact === 'true',
        assessment_text: impactForm.assessment_text.trim() || undefined,
        target_date: impactForm.target_date || undefined,
        owner_name: impactForm.owner_name.trim() || undefined,
      });
      setImpactForm({ ...impactForm, assessment_text: '', target_date: '', owner_name: '' });
    } catch { /* surfaced via addImpact.isError */ }
  }
  const liveRpn = Number(qraForm.severity) * Number(qraForm.occurrence) * Number(qraForm.detectability);
  async function submitQra() {
    try {
      await addQra.mutateAsync({
        subject: qraForm.subject.trim() || undefined,
        severity: Number(qraForm.severity), occurrence: Number(qraForm.occurrence), detectability: Number(qraForm.detectability),
        mitigation: qraForm.mitigation.trim() || undefined,
        residual_severity: qraForm.rsev ? Number(qraForm.rsev) : undefined,
        residual_occurrence: qraForm.rocc ? Number(qraForm.rocc) : undefined,
        residual_detectability: qraForm.rdet ? Number(qraForm.rdet) : undefined,
        decision: qraForm.decision || undefined,
      });
      setQraForm({ ...qraForm, subject: '', mitigation: '', rsev: '', rocc: '', rdet: '', decision: '' });
    } catch { /* surfaced via addQra.isError */ }
  }
  async function submitApproval() {
    try {
      await addAppr.mutateAsync({
        stage: apprForm.stage, decision: apprForm.decision,
        approver_name: apprForm.approver_name.trim() || undefined,
        comments: apprForm.comments.trim() || undefined,
      });
      setApprForm({ ...apprForm, approver_name: '', comments: '' });
    } catch { /* surfaced via addAppr.isError */ }
  }
  async function submitEff() {
    try {
      await addEff.mutateAsync({
        review_due_date: effForm.review_due_date || undefined,
        outcome: effForm.outcome || undefined,
        objective_achieved: effForm.objective_achieved === 'true' ? true : effForm.objective_achieved === 'false' ? false : null,
        capa_ref: effForm.capa_ref.trim() || undefined,
      });
      setEffForm({ ...effForm, review_due_date: '', capa_ref: '' });
    } catch { /* surfaced via addEff.isError */ }
  }
  async function submitAction() {
    if (!actionForm.description.trim()) return;
    try {
      await addAction.mutateAsync({
        description: actionForm.description.trim(),
        category: actionForm.category,
        owner_name: actionForm.owner_name.trim() || undefined,
        target_date: actionForm.target_date || undefined,
      });
      setActionForm({ ...actionForm, description: '', owner_name: '', target_date: '' });
    } catch { /* surfaced via addAction.isError */ }
  }
  function pickEvidenceFile(file: File | null) {
    if (!file) { setEvFile(null); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || '');
      const base64 = res.includes(',') ? res.slice(res.indexOf(',') + 1) : res;
      setEvFile({ name: file.name, mime: file.type || 'application/octet-stream', base64 });
    };
    reader.readAsDataURL(file);
  }
  async function submitEvidence() {
    if (!evForm.title.trim() && !evFile) return;
    try {
      await addEvidence.mutateAsync({
        category: evForm.category,
        title: evForm.title.trim() || undefined,
        filename: evFile?.name,
        mime: evFile?.mime,
        artifactBase64: evFile?.base64,
        notes: evForm.notes.trim() || undefined,
      });
      setEvForm({ ...evForm, title: '', notes: '' });
      setEvFile(null);
    } catch { /* surfaced via addEvidence.isError */ }
  }
  // Required-evidence checklist: a change type's required_assessments
  // (VALIDATION/REGULATORY/TRAINING) map 1:1 to evidence categories. Each is
  // satisfied once ≥1 evidence row of that category is present.
  const typeRule = (typesQ.data?.items ?? []).find((x) => x.change_type === r.change_type);
  const requiredEvidence = typeRule?.required_assessments ?? [];

  // CC-11 sub-assessments: kind-aware submit + a compact detail summary per row.
  const tSub = (k: string) => t(`changeControl.subAssessment.${k}`);
  const tClosure = (k: string, opts?: Record<string, unknown>) => t(`changeControl.closure.${k}`, opts ?? {});
  const tAudit = (k: string, opts?: Record<string, unknown>) => t(`changeControl.audit.${k}`, opts ?? {});
  const auditOp = (kind: string) => { const op = kind.split('_').pop() || ''; return t(`changeControl.auditOp.${op}`, { defaultValue: op }); };
  const tComm = (k: string, opts?: Record<string, unknown>) => t(`changeControl.committee.${k}`, opts ?? {});
  async function submitCommittee() {
    if (!commForm.member_name.trim()) return;
    try {
      await addCommittee.mutateAsync({ member_name: commForm.member_name.trim(), member_role: commForm.member_role });
      setCommForm({ ...commForm, member_name: '' });
    } catch { /* surfaced via addCommittee.isError */ }
  }
  async function submitSub() {
    if (!subForm.title.trim()) return;
    const base: NewSubAssessmentInput = {
      kind: subForm.kind, title: subForm.title.trim(),
      owner_name: subForm.owner_name.trim() || undefined,
      target_date: subForm.target_date || undefined,
    };
    if (subForm.kind === 'REGULATORY') {
      base.variation_type = subForm.variation_type.trim() || undefined;
      base.country = subForm.country.trim() || undefined;
      base.authority = subForm.authority.trim() || undefined;
      base.submission_before_impl = subForm.submission_before_impl;
    } else if (subForm.kind === 'VALIDATION') {
      base.validation_type = subForm.validation_type || undefined;
      base.protocol_ref = subForm.protocol_ref.trim() || undefined;
      base.report_ref = subForm.report_ref.trim() || undefined;
    } else if (subForm.kind === 'TRAINING') {
      base.training_type = subForm.training_type || undefined;
      base.audience = subForm.audience.trim() || undefined;
      base.document_ref = subForm.document_ref.trim() || undefined;
    }
    try {
      await addSub.mutateAsync(base);
      setSubForm({
        ...subForm, title: '', owner_name: '', target_date: '',
        variation_type: '', country: '', authority: '', submission_before_impl: false,
        protocol_ref: '', report_ref: '', audience: '', document_ref: '',
      });
    } catch { /* surfaced via addSub.isError */ }
  }
  function subDetails(a: CrSubAssessmentRow): string {
    const parts: string[] = [];
    if (a.kind === 'REGULATORY') {
      if (a.variation_type) parts.push(a.variation_type);
      if (a.country) parts.push(a.country);
      if (a.authority) parts.push(a.authority);
      if (a.submission_before_impl) parts.push(tSub('beforeImpl'));
    } else if (a.kind === 'VALIDATION') {
      if (a.validation_type) parts.push(tEnum('valType', a.validation_type));
      if (a.protocol_ref) parts.push(a.protocol_ref);
      if (a.report_ref) parts.push(a.report_ref);
    } else if (a.kind === 'TRAINING') {
      if (a.training_type) parts.push(tEnum('trainType', a.training_type));
      if (a.audience) parts.push(a.audience);
      if (a.document_ref) parts.push(a.document_ref);
    }
    return parts.length ? parts.join(' · ') : '—';
  }

  return (
    <main className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/change-control" className={styles.crumb}>{t('changeControl.register.title')}</Link>
        <span className={styles.crumbSep} aria-hidden>/</span>
        <span>{r.cr_number ?? td('newRequest')}</span>
      </nav>

      <header className={styles.detailHead}>
        <div>
          <h1 className={styles.detailTitle}>{r.cr_number && <span className={styles.mono}>{r.cr_number} · </span>}{r.title}</h1>
          <div className={styles.subline}>
            <span className={styles.statusPill}>{tStatus(r.status)}</span>
            {r.gmp_classification === 'GMP' ? <span className={`${styles.pill} ${styles.cls2}`}>{t('changeControl.register.gmpYes')}</span>
              : r.gmp_classification === 'NON_GMP' ? <span className={`${styles.pill} ${styles.cls3}`}>{t('changeControl.register.gmpNo')}</span> : null}
            {r.change_level && <span className={`${styles.pill} ${levelClass(r.change_level)}`}>{tLevel(r.change_level)}</span>}
            {r.migration_label === 'IMPORTED_DATA_GAP'
              ? <span className={`${styles.pill} ${styles.gapPill}`} title={t('changeControl.register.dataGapTitle', { defaultValue: 'On disk but missing from the official CR Log — QA to reconcile.' })}>{t('changeControl.register.dataGap', { defaultValue: 'Not in CR Log' })}</span>
              : r.migration_label && <span className={`${styles.pill} ${styles.mockPill}`} title={t('changeControl.register.importedTitle', { label: r.migration_label })}>{t('changeControl.register.imported')}</span>}
          </div>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnNeutral}`}
            onClick={() => { void downloadAuthed(`/api/v1/change-control/requests/${id}/pdf`, `${r.cr_number || 'CR'}.pdf`).catch(() => undefined); }}
          >
            ⤓ {td('downloadPdf')}
          </button>
        </div>
      </header>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{td('workflow')}</h2>
        {isLocked && <div className={cc.lockBanner}>🔒 {tAudit('lockedBanner', { status: tStatus(r.status) })}</div>}
        {nextStates.length === 0 ? (
          <span className={styles.terminal}>{td('terminal')}</span>
        ) : (
          <>
            <div className={styles.inlineForm}>
              <input className={styles.textInput} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={td('reasonPh')} />
            </div>
            <div className={styles.actions}>
              {nextStates.map((to) => {
                const danger = to === 'CANCELLED' || to === 'REJECTED';
                const blocked = needsReason(to) && !reason.trim();
                return (
                  <button key={to} type="button" className={`${styles.btn} ${danger ? styles.btnNeutral : styles.btnPrimary}`}
                    disabled={transition.isPending || blocked} onClick={() => void doTransition(to)}>
                    → {tStatus(to)}
                  </button>
                );
              })}
            </div>
            {transition.isError && <div className={styles.err} role="alert">{transition.error.message}</div>}
          </>
        )}
      </section>

      <section className={styles.panel}>
        <div className={styles.detailHead} style={{ marginBottom: 8 }}>
          <h2 className={styles.panelTitle} style={{ margin: 0 }}>{tClosure('title')}</h2>
          <span className={`${styles.pill} ${r.closure_gates.all_passed ? styles.cls3 : styles.cls1}`}>
            {tClosure('ready', { pct: r.closure_gates.ready_pct, passed: r.closure_gates.passed, total: r.closure_gates.total })}
          </span>
        </div>
        <div className={cc.gateGrid}>
          {r.closure_gates.gates.map((g) => (
            <div key={g.key} className={`${cc.gate} ${g.passed ? cc.gatePass : cc.gateFail}`}>
              <span className={cc.gateIcon}>{g.passed ? '✓' : '○'}</span>
              <span className={cc.gateLabel}>{tEnum('gate', g.key)}</span>
              {!g.passed && g.open > 0 ? <span className={cc.gateCount}>{g.open}</span> : null}
            </div>
          ))}
        </div>
        <div className={cc.muted}>{r.closure_gates.all_passed ? tClosure('allClear') : tClosure('blocked')}</div>
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{td('partA')}</h2>
        <dl className={styles.metaGrid}>
          <div><dt>{td('changeType')}</dt><dd>{r.change_type ? tEnum('changeType', r.change_type) : '—'}</dd></div>
          <div><dt>{td('department')}</dt><dd>{s(r.department)}</dd></div>
          <div><dt>{td('initiator')}</dt><dd>{s(r.initiator_name)}</dd></div>
          <div><dt>{td('section')}</dt><dd>{s(r.section)}</dd></div>
          <div><dt>{td('sourceOfChange')}</dt><dd>{r.source_of_change?.length ? r.source_of_change.join(', ') : '—'}</dd></div>
          <div><dt>{td('relatedDeviation')}</dt><dd>{s(r.related_deviation_no)}</dd></div>
          <div><dt>{td('relatedQra')}</dt><dd>{s(r.related_qra_no)}</dd></div>
          <div><dt>{td('duration')}</dt><dd>{s(r.duration)}</dd></div>
          <div><dt>{td('raised')}</dt><dd>{fmtDate(r.created_at_utc)}</dd></div>
          <div><dt>{td('lastUpdated')}</dt><dd>{fmtDT(r.updated_at_utc)}</dd></div>
        </dl>
        {r.description && <div className={styles.descBlock}><strong>{td('description')}</strong><p>{r.description}</p></div>}
        {r.current_practice && <div className={styles.descBlock}><strong>{td('currentPractice')}</strong><p>{r.current_practice}</p></div>}
        {r.proposed_change && <div className={styles.descBlock}><strong>{td('proposedChange')}</strong><p>{r.proposed_change}</p></div>}
        {r.reason && <div className={styles.descBlock}><strong>{td('reasonJust')}</strong><p>{r.reason}</p></div>}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{td('partC')}</h2>
        <dl className={styles.metaGrid}>
          <div><dt>{td('qualityImpact')}</dt><dd>{s(r.quality_impact)}</dd></div>
          <div><dt>{td('safetyImpact')}</dt><dd>{s(r.safety_impact)}</dd></div>
          <div><dt>{td('implementable')}</dt><dd>{yn(r.implementable)}</dd></div>
          <div><dt>{td('effRequired')}</dt><dd>{yn(r.effectiveness_required)}</dd></div>
          <div><dt>{td('effDue')}</dt><dd>{r.effectiveness_due_days != null ? td('days', { n: r.effectiveness_due_days }) : '—'}</dd></div>
        </dl>
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{td('lifecycle')}</h2>
        <dl className={styles.metaGrid}>
          <div><dt>{td('qaPreliminary')}</dt><dd>{fmtDT(r.qa_preliminary_at)}</dd></div>
          <div><dt>{td('finalApproved')}</dt><dd>{fmtDT(r.final_approved_at)}</dd></div>
          <div><dt>{td('implemented')}</dt><dd>{fmtDT(r.implemented_at)}</dd></div>
          <div><dt>{td('closed')}</dt><dd>{fmtDT(r.closed_at)}</dd></div>
        </dl>
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{td('partB')}</h2>
        {r.impacts.length > 0 ? (
          <table className={styles.table}>
            <thead><tr><th>{td('thDepartment')}</th><th>{td('thImpact')}</th><th>{td('thAssessment')}</th><th>{td('thTarget')}</th><th>{td('thOwner')}</th><th aria-label={td('add')} /></tr></thead>
            <tbody>
              {r.impacts.map((i, k) => (
                <tr key={k}>
                  <td>{tEnum('dept', i.department)}</td><td>{yn(i.has_impact)}</td><td>{s(i.assessment_text)}</td><td>{fmtDate(i.target_date as string)}</td><td>{s(i.owner_name)}</td>
                  <td><button type="button" className={cc.delBtn} aria-label="✕" disabled={delImpact.isPending} onClick={() => delImpact.mutate(String(i.impact_id))}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <span className={styles.terminal}>{td('noImpacts')}</span>}
        <div className={cc.addRow}>
          <span className={cc.addLabel}>{td('addImpact')}</span>
          <select value={impactForm.department} onChange={(e) => setImpactForm({ ...impactForm, department: e.target.value })}>
            {CC_DEPARTMENTS.map((d) => <option key={d} value={d}>{tEnum('dept', d)}</option>)}
          </select>
          <select value={impactForm.has_impact} onChange={(e) => setImpactForm({ ...impactForm, has_impact: e.target.value })}>
            <option value="true">{td('impacted')}</option>
            <option value="false">{td('notImpacted')}</option>
          </select>
          <input className={cc.grow} type="text" placeholder={td('assessmentPh')} value={impactForm.assessment_text} onChange={(e) => setImpactForm({ ...impactForm, assessment_text: e.target.value })} />
          <input type="date" aria-label={td('thTarget')} value={impactForm.target_date} onChange={(e) => setImpactForm({ ...impactForm, target_date: e.target.value })} />
          <input type="text" placeholder={td('ownerPh')} value={impactForm.owner_name} onChange={(e) => setImpactForm({ ...impactForm, owner_name: e.target.value })} />
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={addImpact.isPending} onClick={() => void submitImpact()}>{addImpact.isPending ? td('adding') : td('add')}</button>
        </div>
        {addImpact.isError && <div className={styles.err} role="alert">{addImpact.error.message}</div>}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{td('qra')}</h2>
        {r.qra.length > 0 ? (
          <table className={styles.table}>
            <thead><tr><th>{td('thQra')}</th><th>{td('thSubject')}</th><th>S</th><th>O</th><th>D</th><th>RPN</th><th>{td('thCategory')}</th><th>{td('thResidual')}</th><th>{td('thDecision')}</th><th aria-label="✕" /></tr></thead>
            <tbody>
              {r.qra.map((x, k) => (
                <tr key={k}>
                  <td className={styles.mono}>{s(x.qra_number)}</td><td>{s(x.subject)}</td>
                  <td>{s(x.severity)}</td><td>{s(x.occurrence)}</td><td>{s(x.detectability)}</td>
                  <td><span className={`${cc.rpnPill} ${rpnClass(cc, Number(x.rpn))}`}>{s(x.rpn)}</span></td>
                  <td>{tEnum('riskCat', x.risk_category)}</td>
                  <td>{x.residual_rpn != null ? <span className={`${cc.rpnPill} ${rpnClass(cc, Number(x.residual_rpn))}`}>{s(x.residual_rpn)}</span> : '—'}</td>
                  <td>{tEnum('qraDecision', x.decision)}</td>
                  <td><button type="button" className={cc.delBtn} aria-label="✕" disabled={delQra.isPending} onClick={() => delQra.mutate(String(x.qra_id))}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <span className={styles.terminal}>{td('noQra')}</span>}
        <div className={cc.addRow}>
          <span className={cc.addLabel}>{td('addRisk')}</span>
          <input className={cc.grow} type="text" placeholder={td('riskSubjectPh')} value={qraForm.subject} onChange={(e) => setQraForm({ ...qraForm, subject: e.target.value })} />
          <label className={cc.addLabel} style={{ width: 'auto' }}>S
            <select aria-label="Severity" value={qraForm.severity} onChange={(e) => setQraForm({ ...qraForm, severity: e.target.value })}>{SOD.map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
          <label className={cc.addLabel} style={{ width: 'auto' }}>O
            <select aria-label="Occurrence" value={qraForm.occurrence} onChange={(e) => setQraForm({ ...qraForm, occurrence: e.target.value })}>{SOD.map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
          <label className={cc.addLabel} style={{ width: 'auto' }}>D
            <select aria-label="Detectability" value={qraForm.detectability} onChange={(e) => setQraForm({ ...qraForm, detectability: e.target.value })}>{SOD.map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
          <span className={`${cc.rpnPill} ${rpnClass(cc, liveRpn)}`}>RPN {liveRpn} · {tEnum('riskCat', rpnCategory(liveRpn))}</span>
          <input className={cc.grow} type="text" placeholder={td('mitigationPh')} value={qraForm.mitigation} onChange={(e) => setQraForm({ ...qraForm, mitigation: e.target.value })} />
          <label className={cc.addLabel} style={{ width: 'auto' }}>rS
            <select aria-label="Residual severity" value={qraForm.rsev} onChange={(e) => setQraForm({ ...qraForm, rsev: e.target.value })}><option value="">—</option>{SOD.map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
          <label className={cc.addLabel} style={{ width: 'auto' }}>rO
            <select aria-label="Residual occurrence" value={qraForm.rocc} onChange={(e) => setQraForm({ ...qraForm, rocc: e.target.value })}><option value="">—</option>{SOD.map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
          <label className={cc.addLabel} style={{ width: 'auto' }}>rD
            <select aria-label="Residual detectability" value={qraForm.rdet} onChange={(e) => setQraForm({ ...qraForm, rdet: e.target.value })}><option value="">—</option>{SOD.map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
          <select aria-label={td('thDecision')} value={qraForm.decision} onChange={(e) => setQraForm({ ...qraForm, decision: e.target.value })}>
            <option value="">{td('decisionPh')}</option>
            {CC_QRA_DECISIONS.map((d) => <option key={d} value={d}>{tEnum('qraDecision', d)}</option>)}
          </select>
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={addQra.isPending} onClick={() => void submitQra()}>{addQra.isPending ? td('adding') : td('addRiskBtn')}</button>
        </div>
        {addQra.isError && <div className={styles.err} role="alert">{addQra.error.message}</div>}
      </section>

      <section className={styles.panel}>
        <div className={styles.detailHead} style={{ marginBottom: 8 }}>
          <h2 className={styles.panelTitle} style={{ margin: 0 }}>{td('partE')}</h2>
          <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} disabled={convertActions.isPending} onClick={() => convertActions.mutate()}>⇲ {td('autoConvert')}</button>
        </div>
        {r.actions.length > 0 ? (
          <table className={styles.table}>
            <thead><tr><th>#</th><th>{td('thAction')}</th><th>{td('thCategory')}</th><th>{td('thOwner')}</th><th>{td('thTarget')}</th><th>{td('thActionStatus')}</th><th aria-label="✕" /></tr></thead>
            <tbody>
              {r.actions.map((a, k) => {
                const st = String(a.status);
                const next = CC_ACTION_FSM[st] ?? [];
                return (
                  <tr key={k}>
                    <td>{s(a.seq)}</td><td>{s(a.description)}</td><td>{tEnum('dept', a.category)}</td><td>{s(a.owner_name)}</td><td>{fmtDate(a.target_date as string)}</td>
                    <td>
                      <span className={styles.statusPill}>{tEnum('actionStatus', st)}</span>
                      {next.length > 0 && (
                        <select aria-label={td('thActionStatus')} value="" disabled={patchAction.isPending}
                          onChange={(e) => { if (e.target.value) patchAction.mutate({ actionId: String(a.action_id), patch: { status: e.target.value } }); }}>
                          <option value="">→</option>
                          {next.map((ns) => <option key={ns} value={ns}>{tEnum('actionStatus', ns)}</option>)}
                        </select>
                      )}
                    </td>
                    <td><button type="button" className={cc.delBtn} aria-label="✕" disabled={delAction.isPending} onClick={() => delAction.mutate(String(a.action_id))}>✕</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : <span className={styles.terminal}>{td('noActions')}</span>}
        <div className={cc.addRow}>
          <span className={cc.addLabel}>{td('addAction')}</span>
          <input className={cc.grow} type="text" placeholder={td('actionDescPh')} value={actionForm.description} onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })} />
          <select aria-label={td('thCategory')} value={actionForm.category} onChange={(e) => setActionForm({ ...actionForm, category: e.target.value })}>
            {CC_ACTION_CATEGORIES.map((c) => <option key={c} value={c}>{tEnum('dept', c)}</option>)}
          </select>
          <input type="text" placeholder={td('ownerPh')} value={actionForm.owner_name} onChange={(e) => setActionForm({ ...actionForm, owner_name: e.target.value })} />
          <input type="date" aria-label={td('thTarget')} value={actionForm.target_date} onChange={(e) => setActionForm({ ...actionForm, target_date: e.target.value })} />
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={addAction.isPending} onClick={() => void submitAction()}>{addAction.isPending ? td('adding') : td('add')}</button>
        </div>
        {(addAction.isError || convertActions.isError) && <div className={styles.err} role="alert">{(addAction.error ?? convertActions.error)?.message}</div>}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{t('changeControl.evidence.title')}</h2>
        {requiredEvidence.length > 0 && (
          <div className={cc.checklist}>
            <span className={cc.checklistLabel}>{t('changeControl.evidence.checklist')}</span>
            {requiredEvidence.map((catg) => {
              const present = r.evidence.some((e) => e.category === catg);
              return (
                <span key={catg} className={`${cc.rpnPill} ${present ? cc.evDone : cc.evTodo}`}>
                  {present ? '✓' : '○'} {tEnum('evidenceCategory', catg)}
                </span>
              );
            })}
          </div>
        )}
        {r.evidence.length > 0 ? (
          <table className={styles.table}>
            <thead><tr><th>{t('changeControl.evidence.category')}</th><th>{t('changeControl.evidence.titleField')}</th><th>{t('changeControl.evidence.file')}</th><th>{t('changeControl.evidence.thUploaded')}</th><th aria-label="✕" /></tr></thead>
            <tbody>
              {r.evidence.map((e) => (
                <tr key={e.evidence_id}>
                  <td><span className={styles.statusPill}>{tEnum('evidenceCategory', e.category)}</span></td>
                  <td>{s(e.title)}{e.notes ? <div className={cc.muted}>{e.notes}</div> : null}</td>
                  <td>
                    {e.has_file ? (
                      <button type="button" className={`${styles.btn} ${styles.btnNeutral}`}
                        onClick={() => { void downloadAuthed(`/api/v1/change-control/requests/${id}/evidence/${e.evidence_id}/download`, e.filename || 'evidence.bin').catch(() => undefined); }}>
                        ⤓ {s(e.filename)}
                      </button>
                    ) : '—'}
                  </td>
                  <td>{fmtDT(e.uploaded_at_utc)}</td>
                  <td><button type="button" className={cc.delBtn} aria-label="✕" disabled={delEvidence.isPending} onClick={() => delEvidence.mutate(e.evidence_id)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <span className={styles.terminal}>{t('changeControl.evidence.noEvidence')}</span>}
        <div className={cc.addRow}>
          <span className={cc.addLabel}>{t('changeControl.evidence.addEvidence')}</span>
          <select aria-label={t('changeControl.evidence.category')} value={evForm.category} onChange={(e) => setEvForm({ ...evForm, category: e.target.value })}>
            {CC_EVIDENCE_CATEGORIES.map((c) => <option key={c} value={c}>{tEnum('evidenceCategory', c)}</option>)}
          </select>
          <input className={cc.grow} type="text" placeholder={t('changeControl.evidence.titlePh')} value={evForm.title} onChange={(e) => setEvForm({ ...evForm, title: e.target.value })} />
          <input type="file" aria-label={t('changeControl.evidence.file')} onChange={(e) => pickEvidenceFile(e.target.files?.[0] ?? null)} />
          <input type="text" placeholder={t('changeControl.evidence.notes')} value={evForm.notes} onChange={(e) => setEvForm({ ...evForm, notes: e.target.value })} />
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={addEvidence.isPending} onClick={() => void submitEvidence()}>{addEvidence.isPending ? t('changeControl.evidence.uploading') : t('changeControl.evidence.upload')}</button>
        </div>
        {addEvidence.isError && <div className={styles.err} role="alert">{addEvidence.error.message}</div>}
      </section>

      <section className={styles.panel}>
        <div className={styles.detailHead} style={{ marginBottom: 8 }}>
          <h2 className={styles.panelTitle} style={{ margin: 0 }}>{tSub('title')}</h2>
          <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} disabled={genSub.isPending || !r.change_type}
            onClick={() => genSub.mutate()} title={!r.change_type ? tSub('generate') : undefined}>✨ {tSub('generate')}</button>
        </div>
        {r.sub_assessments.length > 0 ? (
          <table className={styles.table}>
            <thead><tr><th>{tSub('kind')}</th><th>{tSub('thTitle')}</th><th>{tSub('thDetails')}</th><th>{tSub('thStatus')}</th><th>{tSub('thOwner')}</th><th>{tSub('thTarget')}</th><th aria-label="✕" /></tr></thead>
            <tbody>
              {r.sub_assessments.map((a) => {
                const st = String(a.status);
                return (
                  <tr key={a.sub_id}>
                    <td><span className={`${styles.pill} ${styles.cls2}`}>{tEnum('subKind', a.kind)}</span></td>
                    <td>{s(a.title)}{a.auto_generated ? <div className={cc.muted}>{tSub('auto')}{a.linked_action_id ? ` · ${tSub('linkedAction')}` : ''}</div> : null}</td>
                    <td>{subDetails(a)}</td>
                    <td>
                      <span className={styles.statusPill}>{tEnum('subStatus', st)}</span>
                      <select aria-label={tSub('thStatus')} value="" disabled={patchSub.isPending}
                        onChange={(e) => { if (e.target.value) patchSub.mutate({ subId: a.sub_id, patch: { status: e.target.value } }); }}>
                        <option value="">→</option>
                        {CC_SUB_STATUS.filter((x) => x !== st).map((ns) => <option key={ns} value={ns}>{tEnum('subStatus', ns)}</option>)}
                      </select>
                    </td>
                    <td>{s(a.owner_name)}</td>
                    <td>{fmtDate(a.target_date)}</td>
                    <td><button type="button" className={cc.delBtn} aria-label="✕" disabled={delSub.isPending} onClick={() => delSub.mutate(a.sub_id)}>✕</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : <span className={styles.terminal}>{tSub('none')}</span>}
        <div className={cc.addRow}>
          <span className={cc.addLabel}>{tSub('add')}</span>
          <select aria-label={tSub('kind')} value={subForm.kind} onChange={(e) => setSubForm({ ...subForm, kind: e.target.value })}>
            {CC_SUB_KINDS.map((k) => <option key={k} value={k}>{tEnum('subKind', k)}</option>)}
          </select>
          <input className={cc.grow} type="text" placeholder={tSub('titlePh')} value={subForm.title} onChange={(e) => setSubForm({ ...subForm, title: e.target.value })} />
          {subForm.kind === 'REGULATORY' && (<>
            <input type="text" placeholder={tSub('variationType')} value={subForm.variation_type} onChange={(e) => setSubForm({ ...subForm, variation_type: e.target.value })} />
            <input type="text" placeholder={tSub('country')} value={subForm.country} onChange={(e) => setSubForm({ ...subForm, country: e.target.value })} />
            <input type="text" placeholder={tSub('authority')} value={subForm.authority} onChange={(e) => setSubForm({ ...subForm, authority: e.target.value })} />
            <label className={cc.chk}><input type="checkbox" checked={subForm.submission_before_impl} onChange={(e) => setSubForm({ ...subForm, submission_before_impl: e.target.checked })} /> {tSub('beforeImpl')}</label>
          </>)}
          {subForm.kind === 'VALIDATION' && (<>
            <select aria-label={tSub('validationType')} value={subForm.validation_type} onChange={(e) => setSubForm({ ...subForm, validation_type: e.target.value })}>
              {CC_VALIDATION_TYPES.map((v) => <option key={v} value={v}>{tEnum('valType', v)}</option>)}
            </select>
            <input type="text" placeholder={tSub('protocolRef')} value={subForm.protocol_ref} onChange={(e) => setSubForm({ ...subForm, protocol_ref: e.target.value })} />
            <input type="text" placeholder={tSub('reportRef')} value={subForm.report_ref} onChange={(e) => setSubForm({ ...subForm, report_ref: e.target.value })} />
          </>)}
          {subForm.kind === 'TRAINING' && (<>
            <select aria-label={tSub('trainingType')} value={subForm.training_type} onChange={(e) => setSubForm({ ...subForm, training_type: e.target.value })}>
              {CC_TRAINING_TYPES.map((v) => <option key={v} value={v}>{tEnum('trainType', v)}</option>)}
            </select>
            <input type="text" placeholder={tSub('audience')} value={subForm.audience} onChange={(e) => setSubForm({ ...subForm, audience: e.target.value })} />
            <input type="text" placeholder={tSub('documentRef')} value={subForm.document_ref} onChange={(e) => setSubForm({ ...subForm, document_ref: e.target.value })} />
          </>)}
          <input type="text" placeholder={tSub('thOwner')} value={subForm.owner_name} onChange={(e) => setSubForm({ ...subForm, owner_name: e.target.value })} />
          <input type="date" aria-label={tSub('thTarget')} value={subForm.target_date} onChange={(e) => setSubForm({ ...subForm, target_date: e.target.value })} />
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={addSub.isPending} onClick={() => void submitSub()}>{addSub.isPending ? td('adding') : td('add')}</button>
        </div>
        {(addSub.isError || genSub.isError) && <div className={styles.err} role="alert">{(addSub.error ?? genSub.error)?.message}</div>}
      </section>

      <section className={styles.panel}>
        <div className={styles.detailHead} style={{ marginBottom: 8 }}>
          <h2 className={styles.panelTitle} style={{ margin: 0 }}>{tComm('title')}</h2>
          {r.committee.length > 0 && (
            <span className={`${styles.pill} ${r.committee_summary.decision === 'APPROVED' ? styles.cls3 : r.committee_summary.decision === 'REJECTED' ? styles.cls1 : styles.cls2}`}>
              {tEnum('committeeDecision', r.committee_summary.decision)} · {tComm('voted', { voted: r.committee_summary.voted, total: r.committee_summary.total })} · {tComm('quorum')}: {r.committee_summary.quorum_met ? tComm('met') : tComm('notMet')}
            </span>
          )}
        </div>
        {r.committee.length > 0 ? (
          <table className={styles.table}>
            <thead><tr><th>{tComm('thMember')}</th><th>{tComm('thRole')}</th><th>{tComm('thVote')}</th><th>{tComm('thComments')}</th><th aria-label="✕" /></tr></thead>
            <tbody>
              {r.committee.map((mem) => (
                <tr key={mem.member_id}>
                  <td>{s(mem.member_name)}</td>
                  <td>{tEnum('committeeRole', mem.member_role)}</td>
                  <td>
                    <span className={styles.statusPill}>{tEnum('committeeVote', mem.vote)}</span>
                    <select aria-label={tComm('thVote')} value="" disabled={patchCommittee.isPending}
                      onChange={(e) => { if (e.target.value) patchCommittee.mutate({ memberId: mem.member_id, patch: { vote: e.target.value } }); }}>
                      <option value="">→</option>
                      {CC_COMMITTEE_VOTES.filter((v) => v !== mem.vote).map((v) => <option key={v} value={v}>{tEnum('committeeVote', v)}</option>)}
                    </select>
                  </td>
                  <td>
                    <input className={cc.grow} type="text" defaultValue={mem.comments ?? ''} placeholder={tComm('commentPh')}
                      onBlur={(e) => { if ((e.target.value || '') !== (mem.comments ?? '')) patchCommittee.mutate({ memberId: mem.member_id, patch: { comments: e.target.value } }); }} />
                  </td>
                  <td><button type="button" className={cc.delBtn} aria-label="✕" disabled={delCommittee.isPending} onClick={() => delCommittee.mutate(mem.member_id)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <span className={styles.terminal}>{tComm('none')}</span>}
        <div className={cc.addRow}>
          <span className={cc.addLabel}>{tComm('add')}</span>
          <input className={cc.grow} type="text" placeholder={tComm('namePh')} value={commForm.member_name} onChange={(e) => setCommForm({ ...commForm, member_name: e.target.value })} />
          <select aria-label={tComm('thRole')} value={commForm.member_role} onChange={(e) => setCommForm({ ...commForm, member_role: e.target.value })}>
            {CC_COMMITTEE_ROLES.map((rr) => <option key={rr} value={rr}>{tEnum('committeeRole', rr)}</option>)}
          </select>
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={addCommittee.isPending} onClick={() => void submitCommittee()}>{addCommittee.isPending ? td('adding') : td('add')}</button>
        </div>
        <div className={cc.muted}>{tComm('minutesHint')}</div>
        {(addCommittee.isError || patchCommittee.isError) && <div className={styles.err} role="alert">{(addCommittee.error ?? patchCommittee.error)?.message}</div>}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{td('approvals')}</h2>
        {r.approvals.length > 0 ? (
          <table className={styles.table}>
            <thead><tr><th>{td('thStage')}</th><th>{td('thDecision')}</th><th>{td('thApprover')}</th><th>{td('thDecided')}</th><th>{td('thComments')}</th><th aria-label="✕" /></tr></thead>
            <tbody>
              {r.approvals.map((a, k) => (
                <tr key={k}>
                  <td>{tEnum('apprStage', a.stage)}</td><td>{tEnum('apprDecision', a.decision)}</td><td>{s(a.approver_name)}</td><td>{fmtDT(a.decided_at as string)}</td><td>{s(a.comments)}</td>
                  <td><button type="button" className={cc.delBtn} aria-label="✕" disabled={delAppr.isPending} onClick={() => delAppr.mutate(String(a.approval_id))}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <span className={styles.terminal}>{td('noApprovals')}</span>}
        <div className={cc.addRow}>
          <span className={cc.addLabel}>{td('recordApproval')}</span>
          <select aria-label={td('thStage')} value={apprForm.stage} onChange={(e) => setApprForm({ ...apprForm, stage: e.target.value })}>
            {CC_APPROVAL_STAGES.map((st) => <option key={st} value={st}>{tEnum('apprStage', st)}</option>)}
          </select>
          <select aria-label={td('thDecision')} value={apprForm.decision} onChange={(e) => setApprForm({ ...apprForm, decision: e.target.value })}>
            {CC_APPROVAL_DECISIONS.map((d) => <option key={d} value={d}>{tEnum('apprDecision', d)}</option>)}
          </select>
          <input type="text" placeholder={td('approverPh')} value={apprForm.approver_name} onChange={(e) => setApprForm({ ...apprForm, approver_name: e.target.value })} />
          <input className={cc.grow} type="text" placeholder={td('commentsPh')} value={apprForm.comments} onChange={(e) => setApprForm({ ...apprForm, comments: e.target.value })} />
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={addAppr.isPending} onClick={() => void submitApproval()}>{addAppr.isPending ? td('adding') : td('add')}</button>
        </div>
        {addAppr.isError && <div className={styles.err} role="alert">{addAppr.error.message}</div>}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{td('effectiveness')}</h2>
        {r.effectiveness.length > 0 ? (
          <table className={styles.table}>
            <thead><tr><th>{td('thDue')}</th><th>{td('thOutcome')}</th><th>{td('thObjectiveMet')}</th><th>{td('thAdverse')}</th><th>{td('thCapa')}</th><th aria-label="✕" /></tr></thead>
            <tbody>
              {r.effectiveness.map((e, k) => (
                <tr key={k}>
                  <td>{fmtDate(e.review_due_date as string)}</td><td>{tEnum('effOutcome', e.outcome)}</td><td>{yn(e.objective_achieved)}</td><td>{yn(e.adverse_impact)}</td><td>{s(e.capa_ref)}</td>
                  <td><button type="button" className={cc.delBtn} aria-label="✕" disabled={delEff.isPending} onClick={() => delEff.mutate(String(e.eff_id))}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <span className={styles.terminal}>{td('noEff')}</span>}
        <div className={cc.addRow}>
          <span className={cc.addLabel}>{td('recordEff')}</span>
          <label className={cc.addLabel} style={{ width: 'auto' }}>{td('due')}
            <input type="date" aria-label={td('due')} value={effForm.review_due_date} onChange={(e) => setEffForm({ ...effForm, review_due_date: e.target.value })} /></label>
          <select aria-label={td('thOutcome')} value={effForm.outcome} onChange={(e) => setEffForm({ ...effForm, outcome: e.target.value })}>
            {CC_EFF_OUTCOMES.map((o) => <option key={o} value={o}>{tEnum('effOutcome', o)}</option>)}
          </select>
          <select aria-label={td('thObjectiveMet')} value={effForm.objective_achieved} onChange={(e) => setEffForm({ ...effForm, objective_achieved: e.target.value })}>
            <option value="true">{td('objectiveMet')}</option>
            <option value="false">{td('notMet')}</option>
          </select>
          <input type="text" placeholder={td('capaPh')} value={effForm.capa_ref} onChange={(e) => setEffForm({ ...effForm, capa_ref: e.target.value })} />
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={addEff.isPending} onClick={() => void submitEff()}>{addEff.isPending ? td('adding') : td('add')}</button>
        </div>
        {addEff.isError && <div className={styles.err} role="alert">{addEff.error.message}</div>}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>{td('history')}</h2>
        {r.history.length === 0 ? <div className={styles.empty}>{td('noHistory')}</div> : (
          <ol className={styles.timeline}>
            {r.history.map((h) => (
              <li key={h.history_id} className={styles.tlRow}>
                <span className={styles.tlWhen}>{fmtDT(h.event_at_utc)}</span>
                <span className={styles.tlWhat}>
                  {h.event_type === 'TRANSITION' && h.from_status
                    ? `${tStatus(h.from_status)} → ${tStatus(h.to_status ?? '')}`
                    : h.event_type.replaceAll('_', ' ').toLowerCase()}
                  {h.reason ? <span className={styles.tlNote}> — {h.reason}</span> : null}
                </span>
                {h.actor_name && <span className={styles.sub}>{h.actor_name}</span>}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className={styles.panel}>
        <div className={styles.detailHead} style={{ marginBottom: 8 }}>
          <h2 className={styles.panelTitle} style={{ margin: 0 }}>{tAudit('title')}</h2>
          <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setShowAudit((v) => !v)}>
            {showAudit ? tAudit('hide') : tAudit('show')}
          </button>
        </div>
        {showAudit && (
          auditQ.isLoading ? <div className={styles.empty}>{tAudit('loading')}</div>
            : (auditQ.data?.items.length ?? 0) === 0 ? <span className={styles.terminal}>{tAudit('none')}</span>
              : (
                <table className={styles.table}>
                  <thead><tr><th>{tAudit('thEvent')}</th><th>{tAudit('thRecord')}</th><th>{tAudit('thActor')}</th><th>{tAudit('thWhen')}</th></tr></thead>
                  <tbody>
                    {(auditQ.data?.items ?? []).map((a) => {
                      const op = a.event_kind.split('_').pop() || '';
                      const cls = op === 'INSERT' ? cc.opAdd : op === 'DELETE' ? cc.opDel : cc.opUpd;
                      return (
                        <tr key={a.audit_id}>
                          <td><span className={`${cc.opBadge} ${cls}`}>{auditOp(a.event_kind)}</span> <span className={styles.mono}>{a.event_kind}</span></td>
                          <td>{s(a.target_entity_type)}</td>
                          <td>{s(a.actor_user_id)}</td>
                          <td>{fmtDT(a.recorded_at_utc)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
        )}
      </section>
    </main>
  );
}
